import { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import SEOHead from '../components/common/SEOHead';
import { usePageSeo } from '../hooks/useSeoConfig';
import axios from 'axios';
import { getProvinceName } from '../data/canadianProvinces';
import { ExternalLink, MapPin } from 'lucide-react';

// ─── Partner school card ──────────────────────────────────────────────────────

const SchoolInitials = ({ name }) => {
    const words = name.trim().split(/\s+/).filter(Boolean);
    const initials = words.length >= 2
        ? (words[0][0] + words[1][0]).toUpperCase()
        : name.slice(0, 2).toUpperCase();
    return (
        <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center">
            <span className="text-white font-black text-4xl tracking-wide">{initials}</span>
        </div>
    );
};

const SchoolCard = ({ school }) => {
    const { language } = useLanguage();
    const [logoError, setLogoError] = useState(false);
    const provinceLabel = getProvinceName(school.province, language);
    // EN visitors get websiteEn when set; fallback to the FR/default URL
    const websiteRaw = (language === 'en' && school.websiteEn) ? school.websiteEn : school.website;
    const websiteHref = websiteRaw
        ? (websiteRaw.startsWith('http') ? websiteRaw : `https://${websiteRaw}`)
        : null;

    return (
        <div className="bg-white dark:bg-white/[0.03] rounded-2xl border border-gray-200 dark:border-white/[0.08] overflow-hidden shadow-sm hover:shadow-md transition-shadow flex flex-col group">

            {/* Logo zone */}
            <div className="flex items-center justify-center h-48 bg-gray-50 dark:bg-white/5 border-b border-gray-100 dark:border-white/5 p-6">
                {school.logoUrl && !logoError ? (
                    <img
                        src={school.logoUrl}
                        alt={school.name}
                        className="max-h-full max-w-full object-contain"
                        onError={() => setLogoError(true)}
                    />
                ) : (
                    <SchoolInitials name={school.name} />
                )}
            </div>

            {/* Info + button */}
            <div className="p-5 flex flex-col gap-3 flex-1">
                <div className="flex-1">
                    <p className="font-bold text-gray-900 dark:text-white text-base leading-snug">{school.name}</p>
                    {(school.ville || provinceLabel) && (
                        <p className="flex items-center gap-1 text-sm text-gray-500 dark:text-white/50 mt-1">
                            <MapPin size={12} className="shrink-0" />
                            {[school.ville, provinceLabel].filter(Boolean).join(', ')}
                        </p>
                    )}
                </div>

                {websiteHref ? (
                    <a
                        href={websiteHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white text-sm font-semibold transition-colors"
                    >
                        <ExternalLink size={14} />
                        {language === 'fr' ? 'Visiter le site' : 'Visit website'}
                    </a>
                ) : (
                    <div className="flex items-center justify-center px-4 py-2.5 rounded-xl bg-gray-100 dark:bg-white/5 text-gray-400 text-sm cursor-default select-none">
                        {language === 'fr' ? 'Site non disponible' : 'Website unavailable'}
                    </div>
                )}
            </div>
        </div>
    );
};

// ─── Partnership form card ────────────────────────────────────────────────────

const FormCard = ({ type, data, language, formData, onChange, onSubmit }) => {
    const title      = language === 'fr' ? data.titleFr      : data.titleEn;
    const subtitle   = language === 'fr' ? data.subtitleFr   : data.subtitleEn;
    const sendLabel  = language === 'fr' ? 'Envoyer' : 'Send';

    return (
        <div className="bg-white dark:bg-white/[0.03] rounded-2xl border border-gray-200 dark:border-white/[0.08] overflow-hidden shadow-sm">
            {/* Card header */}
            <div className="px-6 py-5 border-b border-gray-100 dark:border-white/5 bg-gray-50 dark:bg-white/5 flex items-center gap-4">
                <div className="p-3 bg-blue-100 dark:bg-blue-500/20 rounded-2xl flex-shrink-0">
                    <img src={data.icon} alt={title} className="w-8 h-8 object-contain" />
                </div>
                <div>
                    <h2 className="text-base font-bold text-gray-900 dark:text-white">{title}</h2>
                    <p className="text-sm text-gray-500 dark:text-white/50 mt-0.5 leading-snug">{subtitle}</p>
                </div>
            </div>

            {/* Form */}
            <div className="px-6 py-6 space-y-4">
                {data.fields.map((field) => {
                    const label = language === 'fr' ? field.labelFr : field.labelEn;
                    const placeholder = language === 'fr' ? field.placeholderFr : field.placeholderEn;
                    const baseClass = "w-full px-4 py-2.5 bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/[0.08] rounded-xl text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 transition";
                    return (
                        <div key={field.id}>
                            <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5">{label}</label>
                            {field.isTextarea ? (
                                <textarea
                                    rows={3}
                                    className={baseClass}
                                    placeholder={placeholder}
                                    value={formData[field.id]}
                                    onChange={(e) => onChange(e, type, field.id)}
                                />
                            ) : (
                                <input
                                    type="text"
                                    className={baseClass}
                                    placeholder={placeholder}
                                    value={formData[field.id]}
                                    onChange={(e) => onChange(e, type, field.id)}
                                />
                            )}
                        </div>
                    );
                })}
                <div className="pt-2">
                    <button
                        type="button"
                        onClick={() => onSubmit(type)}
                        className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl text-sm transition"
                    >
                        {sendLabel}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/backend/api/v1';

const FORM_DEFS = {
    school: {
        titleFr: 'Centre de formation aéronautique',
        titleEn: 'Flight Training Unit',
        subtitleFr: 'Êtes-vous un centre de formation intéressé par un partenariat avec nizus ?',
        subtitleEn: 'Are you a Flight Training Unit interested in partnering with nizus?',
        icon: '/img/partnership/Flight Training Unit.png',
        fields: [
            { id: 'trainingUnit', labelFr: 'Centre de formation *', labelEn: 'Flight Training Unit *', placeholderFr: 'Centre de formation', placeholderEn: 'Flight Training Unit' },
            { id: 'address',      labelFr: 'Adresse *',              labelEn: 'Address *',              placeholderFr: 'Adresse',            placeholderEn: 'Address' },
            { id: 'contactName',  labelFr: 'Nom du contact *',       labelEn: 'Contact Name *',         placeholderFr: 'Nom du contact',     placeholderEn: 'Contact Name' },
            { id: 'phone',        labelFr: 'Téléphone *',            labelEn: 'Phone number *',         placeholderFr: 'Téléphone',          placeholderEn: 'Phone number' },
            { id: 'email',        labelFr: 'E-mail *',               labelEn: 'E-mail *',               placeholderFr: 'E-mail',             placeholderEn: 'E-mail' },
            { id: 'message',      labelFr: 'Commentaire (optionnel)', labelEn: 'Comment (optional)',     placeholderFr: 'Commentaire',        placeholderEn: 'Comment', isTextarea: true },
        ],
    },
    instructor: {
        titleFr: 'Instructeur de vol',
        titleEn: 'Flight Instructor',
        subtitleFr: "Êtes-vous un instructeur de vol ? Rejoignez notre équipe et aidez à diffuser la bonne parole.",
        subtitleEn: 'Are you a flight instructor? Join our team and help spread the word.',
        icon: '/img/partnership/Flight Instructor.png',
        fields: [
            { id: 'name',        labelFr: 'Prénom et nom *',    labelEn: 'First and Last Names *', placeholderFr: 'Prénom et nom',    placeholderEn: 'First and Last Names' },
            { id: 'address',     labelFr: 'Adresse *',          labelEn: 'Address *',              placeholderFr: 'Adresse',          placeholderEn: 'Address' },
            { id: 'phone',       labelFr: 'Téléphone *',        labelEn: 'Phone Number *',         placeholderFr: 'Téléphone',        placeholderEn: 'Phone Number' },
            { id: 'email',       labelFr: 'E-mail *',           labelEn: 'E-mail *',               placeholderFr: 'E-mail',           placeholderEn: 'E-mail' },
            { id: 'flyingSchool',labelFr: 'École de vol *',     labelEn: 'Flying School *',        placeholderFr: 'École de vol',     placeholderEn: 'Flying School' },
            { id: 'message',     labelFr: 'Commentaire (optionnel)', labelEn: 'Comment (optional)', placeholderFr: 'Commentaire',     placeholderEn: 'Comment', isTextarea: true },
        ],
    },
};

const emptyForm = (type) =>
    FORM_DEFS[type].fields.reduce((acc, f) => ({ ...acc, [f.id]: '' }), {});

function Partnership() {
    const { language } = useLanguage();

    const [schools, setSchools]   = useState([]);
    const [loading, setLoading]   = useState(true);
    const [formData, setFormData] = useState({
        school:     emptyForm('school'),
        instructor: emptyForm('instructor'),
    });

    useEffect(() => {
        axios.get(`${API_BASE}/partners.php`)
            .then(res => setSchools(res.data?.schools ?? []))
            .catch(() => setSchools([]))
            .finally(() => setLoading(false));
    }, []);

    const handleChange = (e, type, field) =>
        setFormData(prev => ({ ...prev, [type]: { ...prev[type], [field]: e.target.value } }));

    const handleSubmit = (type) => {
        // TODO: wire to contact/partnership endpoint
        console.log(`${type} form submitted:`, formData[type]);
        setFormData(prev => ({ ...prev, [type]: emptyForm(type) }));
    };

    const t = {
        fr: {
            heroTitle:       'Partenariats',
            heroSub:         'Travaillez avec nizus et développez ensemble.',
            schoolsTitle:    'Nos écoles partenaires',
            schoolsIntro:    'Ces unités de formation au pilotage au Canada font confiance à nizus pour préparer leurs élèves aux examens écrits de Transports Canada.',
            noSchools:       'Les écoles partenaires seront affichées ici prochainement.',
            formTitle:       'Devenir partenaire',
            formIntro:       'Vous êtes un centre de formation ou un instructeur de vol ? Remplissez le formulaire correspondant et nous vous recontacterons.',
        },
        en: {
            heroTitle:       'Partnerships',
            heroSub:         'Work with nizus and grow together.',
            schoolsTitle:    'Our partner schools',
            schoolsIntro:    'These flight training units across Canada trust nizus to prepare their students for Transport Canada written exams.',
            noSchools:       'Partner schools will be listed here soon.',
            formTitle:       'Become a partner',
            formIntro:       'Are you a flight training unit or a flight instructor? Fill out the relevant form and we will get back to you.',
        },
    };
    const tx = t[language] || t.fr;

    const seo = usePageSeo('page_partnership');

    return (
        <>
            <SEOHead
                title={language === 'fr' ? (seo.title_fr || 'Partenariats - Écoles de pilotage') : (seo.title_en || 'Partnerships - Flight Schools')}
                description={language === 'fr' ? seo.desc_fr : seo.desc_en}
                keywords={language === 'fr' ? seo.keywords_fr : seo.keywords_en}
                canonical="/partnership"
            />

            {/* ── Banner ──────────────────────────────────────────────────── */}
            <div className="max-w-5xl mx-auto">
                <div className="relative overflow-hidden rounded-[2rem] p-10 h-64 bg-gradient-to-br from-[#0052CC] via-[#0041A3] to-[#00287A] border border-gray-200 dark:border-white/5 mb-12 shadow-xl text-white">
                    <div className="absolute inset-0 opacity-20 pointer-events-none"
                        style={{ backgroundImage: 'linear-gradient(90deg, transparent 50%, rgba(0,0,0,0.5) 50%)', backgroundSize: '4px 100%' }}
                    />
                    <div className="relative z-10 h-full flex flex-col justify-between">
                        <div className="flex items-center gap-4">
                            <div className="p-3 bg-white/20 rounded-2xl">
                                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                            </div>
                            <h1 className="text-4xl font-black text-white tracking-tight">{tx.heroTitle}</h1>
                        </div>
                        <p className="text-white/70 text-lg max-w-sm leading-snug">{tx.heroSub}</p>
                    </div>
                    <div className="absolute right-[-40px] bottom-[-40px] opacity-20 pointer-events-none">
                        <svg className="w-72 h-72 text-white stroke-[0.5]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                    </div>
                </div>

                {/* ── Partnership forms ────────────────────────────────────── */}
                <section className="mb-14">
                    <div className="mb-8">
                        <h2 className="text-2xl font-black text-gray-900 dark:text-white mb-2">{tx.formTitle}</h2>
                        <p className="text-gray-500 dark:text-white/50 text-[15px] leading-relaxed max-w-2xl">{tx.formIntro}</p>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        <FormCard
                            type="school"
                            data={FORM_DEFS.school}
                            language={language}
                            formData={formData.school}
                            onChange={handleChange}
                            onSubmit={handleSubmit}
                        />
                        <FormCard
                            type="instructor"
                            data={FORM_DEFS.instructor}
                            language={language}
                            formData={formData.instructor}
                            onChange={handleChange}
                            onSubmit={handleSubmit}
                        />
                    </div>
                </section>

                {/* ── Divider ─────────────────────────────────────────────── */}
                <div className="border-t border-gray-200 dark:border-white/[0.08] mb-14" />

                {/* ── Partner schools ─────────────────────────────────────── */}
                <section>
                    <div className="mb-6">
                        <h2 className="text-2xl font-black text-gray-900 dark:text-white mb-2">{tx.schoolsTitle}</h2>
                        <p className="text-gray-500 dark:text-white/50 text-[15px] leading-relaxed max-w-2xl">{tx.schoolsIntro}</p>
                    </div>

                    {loading ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {[1, 2, 3, 4, 5, 6].map(i => (
                                <div key={i} className="h-72 bg-gray-200 dark:bg-white/5 rounded-2xl animate-pulse" />
                            ))}
                        </div>
                    ) : schools.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {schools.map(school => (
                                <SchoolCard key={school.id} school={school} />
                            ))}
                        </div>
                    ) : (
                        <div className="flex items-center gap-3 px-5 py-4 bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/30 rounded-2xl text-blue-700 dark:text-blue-300 text-sm">
                            <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {tx.noSchools}
                        </div>
                    )}
                </section>
            </div>
        </>
    );
}

export default Partnership;
